/**
 * Responsibility: cycle a search box's placeholder through examples.
 *
 * The examples are the point rather than the animation: the catalogue
 * indexes names as printed and knows "autograph" but not "signed", and a
 * static hint showing one phrasing teaches neither. Rotating through three
 * shows the shape of a query that works.
 *
 * Rotation stops while the field is focused and once anything is typed. A
 * placeholder changing under someone who is mid-thought is a distraction,
 * and one changing while they compare it against what they have written is
 * worse.
 */
import { useEffect, useRef, useState } from 'react';

/** Long enough to read, short enough to see a second one before giving up. */
export const ROTATE_MS = 4000;

/** Matches the fade in the component that renders it. */
export const FADE_MS = 400;

export function useRotatingHint(hints: string[], paused: boolean) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    if (paused || hints.length < 2) {
      setVisible(true);
      return undefined;
    }

    const tick = window.setInterval(() => {
      // Fade out, swap while invisible, fade back in. Swapping on the
      // visible element would read as a flicker rather than a change.
      setVisible(false);
      const swap = window.setTimeout(() => {
        setIndex((current) => (current + 1) % hints.length);
        setVisible(true);
      }, FADE_MS);
      timers.current.push(swap);
    }, ROTATE_MS);

    return () => {
      window.clearInterval(tick);
      timers.current.forEach(window.clearTimeout);
      timers.current = [];
    };
  }, [hints, paused]);

  return { hint: hints[index] ?? '', visible };
}
